import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

interface BreadcrumbContextValue {
  /** The current record's name, shown as the final breadcrumb crumb (null on list pages). */
  recordLabel: string | null
  setRecordLabel: (label: string | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined)

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [recordLabel, setRecordLabel] = useState<string | null>(null)
  return (
    <BreadcrumbContext.Provider value={{ recordLabel, setRecordLabel }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumbContext() {
  const ctx = useContext(BreadcrumbContext)
  if (!ctx) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider')
  }
  return ctx
}

/**
 * Register the current record's name as the final breadcrumb crumb. Mirrors
 * `useDocumentTitle`: pass `undefined`/`null` while the record is still loading
 * to keep the base trail (Home > Object), and it clears automatically on unmount
 * so the next page never shows a stale record name.
 */
export function useRecordBreadcrumb(label?: string | null) {
  const { setRecordLabel } = useBreadcrumbContext()
  useEffect(() => {
    setRecordLabel(label && label.trim() ? label : null)
    return () => setRecordLabel(null)
  }, [label, setRecordLabel])
}
